// src/utils/statisticsCalculator.js

// Sensor index categories (0-based for array indexing)
export const SENSOR_CATEGORIES = {
  // Platform sensors
  PLATFORM: [5, 6, 13, 14, 21, 22, 29, 30],

  // Back foot regions
  BACK_LEFT: [0, 1, 2, 3, 4, 5],
  BACK_RIGHT: [24, 25, 26, 27, 28, 29],
  BACK_MID: [8, 9, 10, 11, 12, 13, 16, 17, 18, 19, 20, 21],

  // Front foot regions
  FRONT_RIGHT: [56, 57, 48, 49, 40, 41, 32, 33],
  FRONT_LEFT: [62, 63, 54, 55, 46, 47, 39, 38],
  FRONT_MID: [34, 35, 36, 37, 42, 43, 44, 45, 50, 51, 52, 53, 58, 59, 60, 61],

  // Metatarsal sensors
  METATARSAL: [51, 52, 56, 57, 58, 59, 60, 61, 62, 63],
};

// Combined categories
export const COMBINED_CATEGORIES = {
  BACK: [
    ...SENSOR_CATEGORIES.BACK_LEFT,
    ...SENSOR_CATEGORIES.BACK_RIGHT,
    ...SENSOR_CATEGORIES.BACK_MID
  ],
  
  FRONT: [
    ...SENSOR_CATEGORIES.FRONT_RIGHT,
    ...SENSOR_CATEGORIES.FRONT_LEFT,
    ...SENSOR_CATEGORIES.FRONT_MID
  ],
  
  RIGHT_TOT: [
    ...SENSOR_CATEGORIES.FRONT_RIGHT,
    ...SENSOR_CATEGORIES.BACK_RIGHT
  ],
  
  LEFT_TOT: [
    ...SENSOR_CATEGORIES.FRONT_LEFT,
    ...SENSOR_CATEGORIES.BACK_LEFT
  ]
};

/**
 * Get total force for a category
 */
const getCategoryTotal = (sensorData, categoryIndices) => {
  return categoryIndices.reduce((sum, index) => {
    const sensorKey = index + 1; // Convert to 1-based index
    return sum + (sensorData[sensorKey] || 0);
  }, 0);
};

/**
 * Get average force for a category
 */
const getCategoryAverage = (sensorData, categoryIndices) => {
  if (categoryIndices.length === 0) return 0;
  
  const total = categoryIndices.reduce((sum, index) => {
    const sensorKey = index + 1; // Convert to 1-based index
    return sum + (sensorData[sensorKey] || 0);
  }, 0);
  
  return Math.round(total / categoryIndices.length);
};

/**
 * Calculate region averages for all sensor categories
 * @param {Object} sensorData - Raw sensor values (1-indexed object)
 * @returns {Object} - Average pressure for each region
 */
export const calculateRegionAverages = (sensorData) => {
  if (!sensorData || Object.keys(sensorData).length === 0) {
    return {
      platform: 0,
      backLeft: 0,
      backRight: 0,
      backMid: 0,
      frontRight: 0,
      frontLeft: 0,
      frontMid: 0,
      metatarsal: 0,
      // Combined regions
      back: 0,
      front: 0,
      rightTotal: 0,
      leftTotal: 0,
      // Overall
      total: 0,
    };
  }

  return {
    // Individual regions
    platform: getCategoryAverage(sensorData, SENSOR_CATEGORIES.PLATFORM),
    backLeft: getCategoryAverage(sensorData, SENSOR_CATEGORIES.BACK_LEFT),
    backRight: getCategoryAverage(sensorData, SENSOR_CATEGORIES.BACK_RIGHT),
    backMid: getCategoryAverage(sensorData, SENSOR_CATEGORIES.BACK_MID),
    frontRight: getCategoryAverage(sensorData, SENSOR_CATEGORIES.FRONT_RIGHT),
    frontLeft: getCategoryAverage(sensorData, SENSOR_CATEGORIES.FRONT_LEFT),
    frontMid: getCategoryAverage(sensorData, SENSOR_CATEGORIES.FRONT_MID),
    metatarsal: getCategoryAverage(sensorData, SENSOR_CATEGORIES.METATARSAL),
    
    // Combined regions
    back: getCategoryAverage(sensorData, COMBINED_CATEGORIES.BACK),
    front: getCategoryAverage(sensorData, COMBINED_CATEGORIES.FRONT),
    rightTotal: getCategoryAverage(sensorData, COMBINED_CATEGORIES.RIGHT_TOT),
    leftTotal: getCategoryAverage(sensorData, COMBINED_CATEGORIES.LEFT_TOT),
    
    // Overall average (all 64 sensors)
    total: getCategoryAverage(sensorData, [...Array(64).keys()]),
  };
};

/**
 * Calculate medial-lateral balance
 */
export const calculateMLBalance = (sensorData) => {
  const leftTotal = getCategoryTotal(sensorData, COMBINED_CATEGORIES.LEFT_TOT);
  const rightTotal = getCategoryTotal(sensorData, COMBINED_CATEGORIES.RIGHT_TOT);
  const allIndices = [...Array(64).keys()];
  const totalForce = getCategoryTotal(sensorData, allIndices);
  
  if (totalForce === 0) return 0;
  
  const mlRatio = (leftTotal - rightTotal) / totalForce;
  return mlRatio * 100;
};

/**
 * Calculate front-back balance
 */
export const calculateFBBalance = (sensorData) => {
  const frontTotal = getCategoryTotal(sensorData, COMBINED_CATEGORIES.FRONT);
  const backTotal = getCategoryTotal(sensorData, COMBINED_CATEGORIES.BACK);
  const allIndices = [...Array(64).keys()];
  const totalForce = getCategoryTotal(sensorData, allIndices);
  
  if (totalForce === 0) return 0;
  
  const fbRatio = (backTotal - frontTotal) / totalForce;
  return fbRatio * 100;
};

/**
 * Calculate alignment position from ML and FB balance
 */
export const calculateAlignment = (mlBalance, fbBalance) => {
  const x = (mlBalance + 100) / 2;
  const y = (fbBalance + 100) / 2;
  
  return {
    x: Math.max(0, Math.min(100, x)),
    y: Math.max(0, Math.min(100, y))
  };
};

export const calculateMax = (sensorData) => {
  const values = Object.values(sensorData).filter(v => typeof v === 'number');
    return values.length > 0 ? Math.max(...values) : 0;
};

/**
 * Calculate alignment status based on ML and FB balance criteria
 * Only calculated when on pointe - returns 'off' otherwise
 */
export const calculateAlignmentStatus = (alignment, onPointe) => {
  if (!onPointe) {
    return 'off';
  }
  
  const center = 50;
  const mlDeviation = Math.abs(alignment.x - center);
  const fbDeviation = alignment.y - center;
  
  if (mlDeviation <= 15 && fbDeviation >= -30 && fbDeviation <= -10) {
    return 'good';
  }
  
  if (mlDeviation <= 20 && fbDeviation >= -10 && fbDeviation <= -5) {
    return 'ok';
  }
  
  return 'bad';
};

export const calculateFitStatus = (sensorData, averages, onPointe) => {
  // Can only calculate if on pointe
  if (!onPointe) {
    return {
      x: 0, // neutral
      y: 0, // neutral
      status: 'not_on_pointe',
      issues: [],
      description: 'Off Pointe'
    };
  }

  // Default return structure
  const result = {
    x: 0, // -1 (loose) to 1 (tight)
    y: 0, // -1 (unsupported/sinking) to 1 (collapsing forward)
    status: 'good',
    issues: [],
    description: ''
  };

  // Calculate hotspot metrics (pressure variance/disparity)
  const pressureVariance = calculatePressureVariance(sensorData);
  // const hotspotCount = countHotspots(sensorData, averages.total);

  // Get key ratios
  const frontToBackRatio = averages.back > 0 ? averages.front / averages.back : Infinity;
  const metatarsalToFrontRatio = averages.front > 0 ? averages.metatarsal / averages.front : 0;
  const platformToTotalRatio = averages.total > 0 ? averages.platform / averages.total : 0;

  // Thresholds (tune these based on testing)
  const THRESHOLDS = {
    HIGH_VARIANCE: 0.4,
    HOTSPOT_COUNT: 5,
    LOW_BACK_PRESSURE: 0.15, // back pressure relative to total
    HIGH_FRONT_BACK_RATIO: 4.0,
    COLLAPSING_RATIO: 6.0,
    HIGH_METATARSAL_RATIO: 0.7,
    HIGH_PLATFORM_RATIO: 0.5,
    GOOD_FRONT_RATIO: 0.5, // front should be ~50-70% of total for good fit
    GOOD_BACK_RATIO: 0.2, // back should be ~20-35% of total for good support
  };

  const backToTotalRatio = averages.total > 0 ? averages.back / averages.total : 0;
  const frontToTotalRatio = averages.total > 0 ? averages.front / averages.total : 0;

  // ============ X-AXIS CALCULATION (Loose to Tight) ============
  
  // Check for too tight conditions
  if (metatarsalToFrontRatio > THRESHOLDS.HIGH_METATARSAL_RATIO) {
    result.x += 0.5;
    result.issues.push('High metatarsal pressure - shoe may be too tight');
  }

  // High hotspots on front indicate tightness
  const frontHotspots = countRegionHotspots(sensorData, COMBINED_CATEGORIES.FRONT, averages.total);
  if (frontHotspots > 3) {
    result.x += 0.3;
    result.issues.push('Multiple hotspots on front - pressure not distributed well');
  }

  // Check for loose conditions - low overall pressure, weight not on box front
  if (frontToTotalRatio < THRESHOLDS.GOOD_FRONT_RATIO * 0.7) {
    result.x -= 0.4;
    result.issues.push('Low front pressure - shoe may be too loose');
  }

  // High variance overall indicates poor pressure distribution (can indicate either)
  if (pressureVariance > THRESHOLDS.HIGH_VARIANCE) {
    result.issues.push('High pressure variance - poor pressure distribution');
    // Slight push toward tight if variance is high
    result.x += 0.1;
  }

  // ============ Y-AXIS CALCULATION (Unsupported to Collapsing) ============
  
  // Check for sinking/unsupported (negative Y)
  if (platformToTotalRatio > THRESHOLDS.HIGH_PLATFORM_RATIO) {
    result.y -= 0.6;
    result.issues.push('High platform pressure - shoe is sinking');
  }

  if (backToTotalRatio < THRESHOLDS.LOW_BACK_PRESSURE) {
    result.y -= 0.4;
    result.issues.push('Low back pressure - insufficient support');
  }

  // Check for collapsing forward (positive Y)
  if (frontToBackRatio > THRESHOLDS.COLLAPSING_RATIO && metatarsalToFrontRatio > THRESHOLDS.HIGH_METATARSAL_RATIO) {
    result.y += 0.7;
    result.issues.push('Collapsing forward - excessive front pressure with high metatarsal load');
  } else if (frontToBackRatio > THRESHOLDS.HIGH_FRONT_BACK_RATIO) {
    result.y += 0.4;
    result.issues.push('Weight shifted too far forward');
  }

  // Good support check - weight mostly on front but still good back pressure
  if (frontToTotalRatio >= THRESHOLDS.GOOD_FRONT_RATIO && 
      frontToTotalRatio <= 0.75 &&
      backToTotalRatio >= THRESHOLDS.GOOD_BACK_RATIO) {
    // This is good - pull toward center
    result.y *= 0.5;
  }

  // ============ CLAMP VALUES ============
  result.x = Math.max(-1, Math.min(1, result.x));
  result.y = Math.max(-1, Math.min(1, result.y));

  // ============ DETERMINE STATUS ============
  const magnitude = Math.sqrt(result.x * result.x + result.y * result.y);
  
  if (magnitude < 0.25) {
    result.status = 'good';
    result.description = 'Good fit';
  } else if (magnitude < 0.5) {
    result.status = 'acceptable';
    result.description = 'Acceptable fit';
  } else if (magnitude < 0.75) {
    result.status = 'poor';
    result.description = 'Poor fit';
  } else {
    result.status = 'bad';
    result.description = 'Bad fit';
  }

  // Add specific descriptions based on quadrant
  if (result.x < -0.3 && result.y < -0.3) {
    result.description = 'Shoe is too loose and unsupportive';
  } else if (result.x > 0.3 && result.y < -0.3) {
    result.description = 'Shoe is tight but sinking';
  } else if (result.x < -0.3 && result.y > 0.3) {
    result.description = 'Shoe is loose and dancer is compensating by pushing forward';
  } else if (result.x > 0.3 && result.y > 0.3) {
    result.description = 'Shoe is too tight and dancer is collapsing';
  }

  return result;
};

// ============ HELPER FUNCTIONS ============

const calculatePressureVariance = (sensorData) => {
  const values = Object.values(sensorData).filter(v => typeof v === 'number');
  if (values.length === 0) return 0;
  
  const mean = values.reduce((a, b) => a + b, 0) / values.length;
  const squaredDiffs = values.map(v => Math.pow(v - mean, 2));
  const variance = squaredDiffs.reduce((a, b) => a + b, 0) / values.length;
  
  // Normalize variance (assuming pressure values 0-1)
  return Math.sqrt(variance);
};

const countHotspots = (sensorData, totalAverage, threshold = 1.5) => {
  // Hotspot = sensor reading significantly above average
  const hotspotThreshold = totalAverage * threshold;
  let count = 0;
  
  for (let i = 0; i < 64; i++) {
    const value = sensorData[i] || 0;
    if (value > hotspotThreshold) {
      count++;
    }
  }
  
  return count;
};

const countRegionHotspots = (sensorData, regionIndices, totalAverage, threshold = 1.5) => {
  const hotspotThreshold = totalAverage * threshold;
  let count = 0;
  
  for (const idx of regionIndices) {
    const value = sensorData[idx] || 0;
    if (value > hotspotThreshold) {
      count++;
    }
  }
  
  return count;
};

/**
 * Calculate cumulative load from all sensor readings
 */
export const calculateCumulativeLoad = (leftData, rightData) => {
  const allIndices = [...Array(64).keys()];
  
  const leftTotal = getCategoryTotal(leftData, allIndices);
  const rightTotal = getCategoryTotal(rightData, allIndices);
  
  const totalLoad = leftTotal + rightTotal;
  
  // Use let since we need to reassign, or use ternary
  let maxPossibleLoad;
  if (leftTotal === 0 || rightTotal === 0) {
    maxPossibleLoad = 1023 * 64;
  } else {
    maxPossibleLoad = 1023 * 64 * 2;
  }
  
  return Math.min(100, (totalLoad / maxPossibleLoad) * 100);
};

/**
 * Calculate pointe shoe status
 */
export const calculatePointeShoeStatus = (cumulativeLoad, usageTime) => {
  const loadFactor = (100 - cumulativeLoad) * 0.7;
  const timeFactor = Math.max(0, 100 - (usageTime / 3600) * 10) * 0.3;
  
  return Math.max(0, Math.min(100, loadFactor + timeFactor));
};

// ==================== Session Tracking State ====================

/**
 * Get current time on pointe including any ongoing pointe time
 */
export const getCurrentTimeOnPointe = (tracker) => {
  const now = Date.now();
  let totalMs = tracker.timeOnPointeMs;
  
  if (tracker.leftOnPointe && tracker.leftOnPointeStartTime) {
    totalMs += (now - tracker.leftOnPointeStartTime);
  }
  
  if (tracker.rightOnPointe && tracker.rightOnPointeStartTime) {
    totalMs += (now - tracker.rightOnPointeStartTime);
  }
  
  return Math.floor(totalMs / 1000);
};

/**
 * Get session duration in seconds
 */
export const getSessionDuration = (tracker) => {
  return Math.floor((Date.now() - tracker.sessionStartTime) / 1000);
};

// src/utils/statisticsCalculator.js

// Update createSessionTracker to include alignment counts
export const createSessionTracker = () => ({
  jumps: 0,
  turns: 0,
  timeOnPointeMs: 0,
  lastUpdateTime: null,
  leftOnPointe: false,
  rightOnPointe: false,
  leftOnPointeStartTime: null,
  rightOnPointeStartTime: null,
  sessionStartTime: Date.now(),
  // ADD: Alignment status counts
  leftAlignmentCounts: { good: 0, ok: 0, bad: 0 },
  rightAlignmentCounts: { good: 0, ok: 0, bad: 0 },
  // Transition tracking
  transitions: {
    rollThrough: 0,
    spring: 0,
    jumpLanding: 0,
    unknown: 0
  },
  transitionHistory: [],  // Store last N transitions for analysis
});

// Update updateSessionTracker to track alignment
export const updateSessionTracker = (
  tracker, 
  leftData = null, 
  rightData = null,
  leftAlignmentStatus = null,
  rightAlignmentStatus = null,
  transitionData = null 
) => {
  const now = Date.now();
  const updatedTracker = { ...tracker };
  
  // Process left foot data
  if (leftData) {
    if (leftData.jumpFlag) {
      updatedTracker.jumps++;
      console.log('🦘 Jump detected (left foot)! Total:', updatedTracker.jumps);
    }
    
    if (leftData.turnFlag) {
      updatedTracker.turns++;
      console.log('💫 Turn detected (left foot)! Total:', updatedTracker.turns);
    }
    
    const wasLeftOnPointe = updatedTracker.leftOnPointe;
    updatedTracker.leftOnPointe = leftData.onPointe;
    
    if (leftData.onPointe && !wasLeftOnPointe) {
      updatedTracker.leftOnPointeStartTime = now;
    } else if (!leftData.onPointe && wasLeftOnPointe && updatedTracker.leftOnPointeStartTime) {
      updatedTracker.timeOnPointeMs += (now - updatedTracker.leftOnPointeStartTime);
      updatedTracker.leftOnPointeStartTime = null;
    }
    
    // Track left alignment status (only when on pointe)
    if (leftData.onPointe && leftAlignmentStatus && leftAlignmentStatus !== 'off') {
      if (leftAlignmentStatus === 'good') {
        updatedTracker.leftAlignmentCounts.good++;
      } else if (leftAlignmentStatus === 'ok') {
        updatedTracker.leftAlignmentCounts.ok++;
      } else if (leftAlignmentStatus === 'bad') {
        updatedTracker.leftAlignmentCounts.bad++;
      }
    }
  }
  
  // Process right foot data
  if (rightData) {
    if (rightData.jumpFlag && !(leftData && leftData.jumpFlag)) {
      updatedTracker.jumps++;
      console.log('🦘 Jump detected (right foot)! Total:', updatedTracker.jumps);
    }
    
    if (rightData.turnFlag && !(leftData && leftData.turnFlag)) {
      updatedTracker.turns++;
      console.log('💫 Turn detected (right foot)! Total:', updatedTracker.turns);
    }
    
    const wasRightOnPointe = updatedTracker.rightOnPointe;
    updatedTracker.rightOnPointe = rightData.onPointe;
    
    if (rightData.onPointe && !wasRightOnPointe) {
      updatedTracker.rightOnPointeStartTime = now;
    } else if (!rightData.onPointe && wasRightOnPointe && updatedTracker.rightOnPointeStartTime) {
      updatedTracker.timeOnPointeMs += (now - updatedTracker.rightOnPointeStartTime);
      updatedTracker.rightOnPointeStartTime = null;
    }
    
    // Track right alignment status (only when on pointe)
    if (rightData.onPointe && rightAlignmentStatus && rightAlignmentStatus !== 'off') {
      if (rightAlignmentStatus === 'good') {
        updatedTracker.rightAlignmentCounts.good++;
      } else if (rightAlignmentStatus === 'ok') {
        updatedTracker.rightAlignmentCounts.ok++;
      } else if (rightAlignmentStatus === 'bad') {
        updatedTracker.rightAlignmentCounts.bad++;
      }
    }
  }

  // Track transitions
  if (transitionData && transitionData.type !== 'unknown') {
    updatedTracker.transitions[transitionData.type === 'roll_through' ? 'rollThrough' : 
                               transitionData.type === 'spring' ? 'spring' : 
                               transitionData.type === 'jump_landing' ? 'jumpLanding' : 'unknown']++;
    
    // Keep last 50 transitions for trend analysis
    updatedTracker.transitionHistory = [
      ...updatedTracker.transitionHistory.slice(-49),
      {
        ...transitionData,
        timestamp: Date.now()
      }
    ];
  }
  updatedTracker.lastUpdateTime = now;
  
  return updatedTracker;
  
};

// Update getDefaultStatistics
export const getDefaultStatistics = () => ({
  timeOnPointe: 0,
  rollThroughs: 0,
  springs: 0,
  jumpLandings: 0,
  Lalignment: { x: 50, y: 50 },
  Lalignmentstat: 'off',
  Ralignment: { x: 50, y: 50 },
  Ralignmentstat: 'off',
  Laverages: {
    platform: 0, backLeft: 0, backRight: 0, backMid: 0,
    frontRight: 0, frontLeft: 0, frontMid: 0, metatarsal: 0,
    back: 0, front: 0, rightTotal: 0, leftTotal: 0, total: 0,
  },
  Raverages: {
    platform: 0, backLeft: 0, backRight: 0, backMid: 0,
    frontRight: 0, frontLeft: 0, frontMid: 0, metatarsal: 0,
    back: 0, front: 0, rightTotal: 0, leftTotal: 0, total: 0,
  },
  cumulativeLoad: 0,
  pointeShoeStatus: 100,
  leftFootGoodCount: 0,
  leftFootOkCount: 0,
  leftFootBadCount: 0,
  rightFootGoodCount: 0,
  rightFootOkCount: 0,
  rightFootBadCount: 0,
  leftOnPointe: false,
  rightOnPointe: false,
});

// Update calculateStatistics to use tracker counts
export const calculateStatistics = (
  leftSensorData = {}, 
  rightSensorData = {},
  sessionTracker = null,
  leftOnPointe = false,
  rightOnPointe = false
) => {
  const leftValues = Object.values(leftSensorData);
  const rightValues = Object.values(rightSensorData);
  
  const hasLeftData = leftValues.length > 0;
  const hasRightData = rightValues.length > 0;
  
  if (!hasLeftData && !hasRightData) {
    return getDefaultStatistics();
  }

  // Calculate region averages
  const Laverages = calculateRegionAverages(leftSensorData);
  const Raverages = calculateRegionAverages(rightSensorData);

  // Calculate current alignment for left foot
  let leftAlignment = { x: 50, y: 50 };
  let leftAlignmentStat = 'off';
  
  if (hasLeftData) {
    leftAlignment = calculateAlignment(
      calculateMLBalance(leftSensorData),
      calculateFBBalance(leftSensorData)
    );
    leftAlignmentStat = calculateAlignmentStatus(leftAlignment, leftOnPointe);
  }
  
  // Calculate current alignment for right foot
  let rightAlignment = { x: 50, y: 50 };
  let rightAlignmentStat = 'off';
  
  if (hasRightData) {
    rightAlignment = calculateAlignment(
      calculateMLBalance(rightSensorData),
      calculateFBBalance(rightSensorData)
    );
    rightAlignmentStat = calculateAlignmentStatus(rightAlignment, rightOnPointe);
  }

  // Get cumulative statistics from session tracker
  const timeOnPointe = sessionTracker ? getCurrentTimeOnPointe(sessionTracker) : 0;
  const jumps = sessionTracker ? sessionTracker.jumps : 0;
  const turns = sessionTracker ? sessionTracker.turns : 0;
  
  // Get alignment counts from tracker (accumulated over time)
  const leftFootGoodCount = sessionTracker?.leftAlignmentCounts?.good || 0;
  const leftFootOkCount = sessionTracker?.leftAlignmentCounts?.ok || 0;
  const leftFootBadCount = sessionTracker?.leftAlignmentCounts?.bad || 0;
  const rightFootGoodCount = sessionTracker?.rightAlignmentCounts?.good || 0;
  const rightFootOkCount = sessionTracker?.rightAlignmentCounts?.ok || 0;
  const rightFootBadCount = sessionTracker?.rightAlignmentCounts?.bad || 0;
  
  // Calculate load and shoe status
  const cumulativeLoad = calculateCumulativeLoad(leftSensorData, rightSensorData);
  const pointeShoeStatus = calculatePointeShoeStatus(cumulativeLoad, timeOnPointe);

  return {
    timeOnPointe,
    jumps,
    turns,
    Lalignment: leftAlignment,
    Lalignmentstat: leftAlignmentStat,
    Ralignment: rightAlignment,
    Ralignmentstat: rightAlignmentStat,
    Laverages,
    Raverages,
    cumulativeLoad,
    pointeShoeStatus,
    // Alignment counts (accumulated from tracker)
    leftFootGoodCount,
    leftFootOkCount,
    leftFootBadCount,
    rightFootGoodCount,
    rightFootOkCount,
    rightFootBadCount,
    leftOnPointe,
    rightOnPointe,
    
    // Balance metrics for debugging
    balanceMetrics: {
      left: {
        ml: hasLeftData ? calculateMLBalance(leftSensorData) : 0,
        fb: hasLeftData ? calculateFBBalance(leftSensorData) : 0
      },
      right: {
        ml: hasRightData ? calculateMLBalance(rightSensorData) : 0,
        fb: hasRightData ? calculateFBBalance(rightSensorData) : 0
      }
    }
  };
};

// Helper to get total alignment counts (combined left + right)
export const getTotalAlignmentCounts = (tracker) => {
  if (!tracker) return { good: 0, ok: 0, bad: 0 };
  
  return {
    good: (tracker.leftAlignmentCounts?.good || 0) + (tracker.rightAlignmentCounts?.good || 0),
    ok: (tracker.leftAlignmentCounts?.ok || 0) + (tracker.rightAlignmentCounts?.ok || 0),
    bad: (tracker.leftAlignmentCounts?.bad || 0) + (tracker.rightAlignmentCounts?.bad || 0),
  };
};

// Update getSessionSummary to include alignment counts
export const getSessionSummary = (tracker) => {
  const totalCounts = getTotalAlignmentCounts(tracker);
  
  return {
    totalJumps: tracker.jumps,
    totalTurns: tracker.turns,
    totalTimeOnPointe: getCurrentTimeOnPointe(tracker),
    sessionDuration: getSessionDuration(tracker),
    sessionStartTime: tracker.sessionStartTime,
    alignmentCounts: {
      left: tracker.leftAlignmentCounts,
      right: tracker.rightAlignmentCounts,
      total: totalCounts,
    },
  };
};